/* ============================================
   SEEK KEY — GA4 Enhanced Ecommerce DataLayer
   ============================================

   v4 (23/03/2026):
   - Fix: item_name e item_category agora populados corretamente
   - Fix: item_variant usa public_title (campo correto do meta.product)
   - Fix: add_to_cart disparava 2x (submit + fetch), agora usa flag de dedup
   - Add: item_category (product.type), item_brand (product.vendor)
   - Add: item_sku no custom dimensions

   Eventos: page_view, view_item, add_to_cart
   Formato: GA4 Enhanced Ecommerce
   ============================================ */

(function() {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  // ══════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════

  /* Shopify exposes product data in the global `meta` object on product pages.
     Fields available: id, vendor, type, handle, variants[{id, price, name, public_title, sku}]
     NOTE: meta.product does NOT have a `title` field — use variants[].name or parse from document.title */
  function getShopifyProduct() {
    if (typeof meta !== 'undefined' && meta && meta.product) {
      return meta.product;
    }
    return null;
  }

  function formatPrice(cents) {
    if (typeof cents === 'number') return cents / 100;
    var num = parseFloat(cents);
    return isNaN(num) ? 0 : num;
  }

  /* Extract product title: meta.product has no `title` field.
     Best source is variant.name (e.g. "Abiotic Factor Midia Digital - Steam").
     Fallback: parse document.title removing the store suffix. */
  function getProductTitle(product) {
    if (product && product.variants && product.variants.length > 0) {
      var variantName = product.variants[0].name || '';
      // variant.name often includes " - VariantTitle", strip it for item_name
      if (product.variants.length === 1 && product.variants[0].public_title) {
        var suffix = ' - ' + product.variants[0].public_title;
        if (variantName.endsWith(suffix)) {
          return variantName.slice(0, -suffix.length);
        }
      }
      return variantName;
    }
    // Fallback: document.title minus store name
    var title = document.title || '';
    var separators = [' \u2013 ', ' | ', ' - ', ' \u2014 '];
    for (var i = 0; i < separators.length; i++) {
      var idx = title.indexOf(separators[i]);
      if (idx > 0) return title.substring(0, idx).trim();
    }
    return title;
  }

  function getPageType() {
    var body = document.body;
    if (!body) return 'other';
    var cls = body.className || '';
    if (cls.indexOf('template-product') !== -1) return 'product';
    if (cls.indexOf('template-collection') !== -1) return 'collection';
    if (cls.indexOf('template-cart') !== -1) return 'cart';
    if (cls.indexOf('template-index') !== -1) return 'home';
    if (cls.indexOf('template-search') !== -1) return 'search';
    return 'other';
  }

  function findVariant(product, variantId) {
    if (!product || !product.variants) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(variantId)) {
        return product.variants[i];
      }
    }
    return null;
  }

  function normalizePlatform(type) {
    if (!type) return '';
    var t = type.replace(/^Conta\s+/i, '');
    if (/psn|playstation|ps[0-9]/i.test(t)) return 'PlayStation';
    if (/xbox/i.test(t)) return 'Xbox';
    if (/steam/i.test(t)) return 'Steam';
    return t;
  }

  function buildItem(product, variant) {
    var title = getProductTitle(product);
    var price = variant ? formatPrice(variant.price) : 0;
    return {
      item_id: variant && variant.sku ? variant.sku : (product ? String(product.id) : ''),
      item_name: title,
      item_category: normalizePlatform(product && product.type ? product.type : ''),
      item_brand: product && product.vendor ? product.vendor : '',
      item_variant: variant && variant.public_title ? variant.public_title : '',
      price: price,
      quantity: 1
    };
  }

  // ══════════════════════════════════════
  // PAGE VIEW
  // ══════════════════════════════════════
  function pushPageView() {
    window.dataLayer.push({
      event: 'page_view',
      page_type: getPageType(),
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }

  // ══════════════════════════════════════
  // VIEW ITEM (product page only)
  // ══════════════════════════════════════
  function pushViewItem() {
    var product = getShopifyProduct();
    if (!product) return;

    var variant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
    var item = buildItem(product, variant);

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: 'BRL',
        value: item.price,
        items: [item]
      }
    });
  }

  // ══════════════════════════════════════
  // ADD TO CART
  // ══════════════════════════════════════
  var lastAddToCartTime = 0;

  function pushAddToCart(variantId, quantity) {
    // Dedup: prevent double-firing from submit + fetch intercept
    var now = Date.now();
    if (now - lastAddToCartTime < 1000) return;
    lastAddToCartTime = now;

    var product = getShopifyProduct();
    var variant = product ? findVariant(product, variantId) : null;
    var item = buildItem(product, variant);
    item.quantity = quantity || 1;

    // If no product in meta (e.g. quick-add from collection page), use fallbacks
    if (!item.item_name) {
      item.item_name = document.title.split(' \u2013 ')[0] || document.title.split(' | ')[0] || '';
    }

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'BRL',
        value: item.price * item.quantity,
        items: [item]
      }
    });
  }

  // ══════════════════════════════════════
  // INTERCEPT ADD-TO-CART
  // ══════════════════════════════════════
  function interceptAddToCart() {
    // Intercept form submissions to /cart/add
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;

      var variantInput = form.querySelector('input[name="id"], select[name="id"]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      var variantId = variantInput ? variantInput.value : null;
      var qty = (qtyInput && parseInt(qtyInput.value)) || 1;

      if (variantId) pushAddToCart(variantId, qty);
    });

    // Intercept fetch calls to /cart/add (for AJAX add-to-cart)
    if (window.fetch) {
      var originalFetch = window.fetch;
      window.fetch = function() {
        var url = arguments[0];
        var opts = arguments[1] || {};

        if (typeof url === 'string' && url.indexOf('/cart/add') !== -1 && opts.body) {
          try {
            var body;
            if (typeof opts.body === 'string') {
              body = JSON.parse(opts.body);
            } else if (opts.body instanceof FormData) {
              body = { id: opts.body.get('id'), quantity: opts.body.get('quantity') };
            }
            if (body && body.id) {
              pushAddToCart(body.id, parseInt(body.quantity) || 1);
            }
          } catch (_) {}
        }
        return originalFetch.apply(this, arguments);
      };
    }
  }

  // ══════════════════════════════════════
  // INIT
  // ══════════════════════════════════════
  function init() {
    pushPageView();

    if (getPageType() === 'product') {
      pushViewItem();
    }

    interceptAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
